import { ExternalLink, Globe, SlidersHorizontal } from "lucide-react";
import React from "react";
import { strings } from "@/I18n";
import type {
  SidebarConfig,
  SidebarItem as SidebarConfigItem,
} from "@/v2/components/sidebar";
import { APP_VERSION } from "@/v2/configs/appVersion";
import {
  Sidebar,
  SidebarDivider,
  SidebarExternalLink,
  SidebarFooter,
  SidebarItem,
} from "@/v2/ui/composed/sidebar";
import { Button } from "@/v2/ui/primitives/button";

interface DashboardSidebarProps {
  visibleSidebarConfig: SidebarConfig;
  isMobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  isTourRunning: boolean;
  isSidebarItemSelected: (item: SidebarConfigItem) => boolean;
  onCustomizeClick: () => void;
  currentLanguageLabel: string;
}

export function DashboardSidebar({
  visibleSidebarConfig,
  isMobileOpen,
  onMobileOpenChange,
  isTourRunning,
  isSidebarItemSelected,
  onCustomizeClick,
  currentLanguageLabel,
}: DashboardSidebarProps) {
  return (
    <Sidebar
      isMobileOpen={isMobileOpen}
      onMobileOpenChange={onMobileOpenChange}
      isTourRunning={isTourRunning}
    >
      {visibleSidebarConfig.map((group, groupIndex) => {
        let hasNonExternal = false;
        let externalDividerInserted = false;

        return (
          <React.Fragment key={group.id}>
            {groupIndex > 0 && <SidebarDivider />}
            <SidebarItem
              isHeader={true}
              name={group.name}
              IconComponent={group.icon}
            />
            {group.items.map((item) => {
              const iconProps =
                typeof item.icon === "string"
                  ? { icon: item.icon }
                  : item.icon
                    ? { IconComponent: item.icon }
                    : {};

              if (!item.isExternal) {
                hasNonExternal = true;
              }
              const shouldInsertExternalDivider =
                item.isExternal && hasNonExternal && !externalDividerInserted;
              if (shouldInsertExternalDivider) {
                externalDividerInserted = true;
              }

              return (
                <React.Fragment key={item.id}>
                  {shouldInsertExternalDivider && <SidebarDivider />}
                  {item.isExternal ? (
                    <SidebarExternalLink
                      name={item.name}
                      description={item.description}
                      href={item.externalUrl || "#"}
                      ExternalIcon={ExternalLink}
                      {...iconProps}
                    />
                  ) : (
                    <SidebarItem
                      name={item.name}
                      route={item.route}
                      onClick={item.onClick}
                      isSelected={isSidebarItemSelected(item)}
                      dataTour={item.dataTour}
                      badge={item.badge}
                      badgeColor={item.badgeColor}
                      {...iconProps}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
      <SidebarFooter>
        <div className="tw-flex tw-flex-col tw-gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCustomizeClick}
            className="tw-text-xs tw-h-8 tw-w-full tw-justify-start tw-text-muted-foreground"
          >
            <SlidersHorizontal className="tw-h-3.5 tw-w-3.5 tw-mr-2" />
            <span>{strings("actions.customize")}</span>
          </Button>
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-[11px] tw-text-muted-foreground">
            <div className="tw-flex tw-items-center tw-gap-1.5">
              <Globe className="tw-h-3 tw-w-3" />
              <span>{currentLanguageLabel}</span>
            </div>
            <span className="tw-opacity-40">·</span>
            <span className="tw-font-mono tw-tracking-wide">
              v{APP_VERSION}
            </span>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
